import styled from 'styled-components';
import { Image as SCImage, Text } from '@sitecore-jss/sitecore-jss-react';
import StyledContentRichText from '_components/Corporate/ContentRichText/StyledContentRichText';

import {
  aspect,
  svg,
  mq,
  heading_s,
  buttonReset,
  buttonColors,
  buttonSize,
  focusOutline,
  safeFocusRemoval,
  iconColorOnPrint
} from '_utils/styles';

import Flickity from 'react-flickity-component';

export const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const bgColor = ({ theme }) => theme.colors.modal.container.background;

export const ThumbsContainer = styled.nav`
  flex: none;
`;

export const Thumbs = styled(Flickity)`
  display: flex;

  &:focus {
    ${focusOutline}
    ${safeFocusRemoval}
  }

  flex: none;
  width: 100%;
  height: 48px;
  justify-content: center;
  position: relative;

  ${mq('md')} {
    height: 80px;
  }

  .flickity-viewport {
    width: 100%;
  }

  &:after,
  &:before {
    opacity: 0;
    transition: opacity 0.2s linear;
    content: '';
    position: absolute;
    top: 0;
    width: 24px;
    height: 100%;
    z-index: 1;
  }

  &:before {
    left: 0;
    background-image: linear-gradient(270deg, rgba(4, 28, 44, 0) 0%, ${bgColor} 95%);
  }

  &:after {
    right: 0;
    background-image: linear-gradient(90deg, rgba(4, 28, 44, 0) 0%, ${bgColor} 95%);
  }

  &.fade-left {
    &:before {
      opacity: 1;
    }
  }

  &.fade-right {
    &:after {
      opacity: 1;
    }
  }
`;

export const ThumbContainer = styled.button`
  ${buttonReset()}

  flex: none;
  width: 48px;
  height: 48px;
  margin-left: 8px;

  ${mq('md')} {
    width: 80px;
    height: 80px;
  }

  background: white;

  &.is-selected {
    border: 3px solid ${({ theme }) => theme.colors.element.secondary};

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: rgba(45, 184, 75, 0.25);
      z-index: 1;
    }
  }

  body:not(.safe-focus-removal) &:focus {
    outline: 0;
    border: 3px solid ${({ theme }) => theme.colors.element.focused};
  }
`;

export const Slides = styled.div`
  width: 100%;
  margin-top: 16px;
  position: relative;

  ${mq('md')} {
    margin-top: 32px;
  }
`;

export const Carousel = styled(Flickity)`
  .flickity-viewport {
    width: 100%;
  }
`;

export const ThumbImage = styled(SCImage)`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  object-fit: cover;
  object-position: 50% 50%;
  font-family: 'object-fit: cover; object-position: 50% 50%;';
`;

export const Slide = styled.div`
  width: 100%;
  margin: 0 16px 0 0;
  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
    outline-offset: -2px !important;
  }
`;

export const ImageContainer = styled.figure`
  width: 100%;
  ${aspect(4, 3)}
  /* max-height: 480px; */
  flex: none;
  margin: 0;
`;

export const SlideImage = styled(SCImage)`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  object-fit: contain;
  font-family: 'object-fit: contain;';
`;

export const PrevNextPosition = styled.div`
  position: absolute;
  margin-top: 37.75%; /* half of 75% which is image height (aspect) */
  z-index: 1;
  overflow: visible;
  top: 0;
  transition: transform 0.4s ${({ theme }) => theme.motion.ease};

  &.prev {
    left: 0;
    transform: translate(-50%, -50%);

    ${mq('xl')} {
      transform: translate(-150%, -50%);
    }
  }

  &.next {
    right: 0;
    transform: translate(50%, -50%);

    ${mq('xl')} {
      transform: translate(150%, -50%);
    }
  }
`;

export const PrevNextButton = styled.button`
  ${buttonReset()}
  ${({ theme }) => buttonSize(theme.buttons.default)}
  ${({ theme }) => buttonColors(theme.colors.button.primary)}
  ${safeFocusRemoval}
    width: 32px;
    height: 32px;

  ${mq('md')} {
    width: 40px;
    height: 40px;
  }
  opacity: 1;
  transition: opacity 0.3s linear;
  
  &:disabled {
    opacity: 0;
    pointer-events: none;
    cursor: auto;
  }

  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ArrowWrapper = styled.span`
  flex: none;
  ${svg(7, 12)}
  width: 8px;
  path {
    fill: ${({ theme }) => theme.colors.element.white};
    ${iconColorOnPrint}
  }

  .prev & {
    transform: rotate(180deg);
  }
`;

export const CaptionContainer = styled.figcaption`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.element.white};
  margin-top: 16px;
  opacity: 1;

  a:visited {
    color: inherit !important;
  }

  transition: opacity 0.3s linear;

  ${Slide}:not(.is-selected) & {
    opacity: 0;
    visibility: hidden;
  }

  ${mq('md')} {
    flex-direction: row;
  }
`;

export const SlideCount = styled.span`
  padding-bottom: 8px;
  white-space: nowrap;

  ${mq('md')} {
    padding-right: 32px;
  }
`;

export const Title = styled(Text)`
  ${heading_s}
`;

export const Caption = styled(StyledContentRichText)`
  ${Title} + & {
    margin-top: 1em;
  }

  &:first-child {
    margin-top: 0;
  }
`;

export const Credit = styled(Text)`
  &:first-child {
    margin-top: 0;
  }
`;
